import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface SafetyMeeting {
  id?: string | number;
  status?: string;
  terminal_number?: string;
  topic?: string;
  opened_at?: string;
  closed_at?: string;
  opened_by?: string;
  duration_minutes?: number;
  signature_count?: number;
  safety_meeting_signatures?: unknown[];
  signatures?: unknown[];
}

export interface TerminalOption {
  id: string;
  name: string;
}

export interface SafetySummary {
  total: number;
  open: number;
  closed: number;
  totalSignatures: number;
  last30d: number;
  terminalsCovered: number;
  latestOpenedAt: number | null;
}

interface SafetyHistoryResponse {
  meetings?: SafetyMeeting[];
  total?: number;
  error?: string;
}

const DEFAULT_TERMINAL_OPTIONS: TerminalOption[] = [{ id: 'all', name: 'All terminals' }];

export function useSafetyReadModel(terminalFilter: string, startDate: string, endDate: string) {
  const [meetings, setMeetings] = useState<SafetyMeeting[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [terminalOptions, setTerminalOptions] = useState<TerminalOption[]>(DEFAULT_TERMINAL_OPTIONS);

  // One-shot unfiltered discovery of terminals that have safety-meeting history.
  // Options must NOT shrink when the caller narrows `terminalFilter`, so we
  // populate this list independently of the filtered meeting query below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', '200');
        const data = await apiFetch<SafetyHistoryResponse>(`/api/safety-meeting/history?${params.toString()}`);
        if (cancelled) return;
        if (data?.error) return; // permission or server error — keep default options
        const seen = new Map<string, TerminalOption>();
        (data.meetings || []).forEach((m) => {
          const t = m.terminal_number;
          if (!t) return;
          const key = String(t);
          if (!seen.has(key)) seen.set(key, { id: key, name: `Terminal ${t}` });
        });
        const sorted = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
        setTerminalOptions([...DEFAULT_TERMINAL_OPTIONS, ...sorted]);
      } catch {
        /* network error — keep default options */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (terminalFilter && terminalFilter !== 'all') params.set('terminal', terminalFilter);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      params.set('limit', '50');

      const data = await apiFetch<SafetyHistoryResponse>(`/api/safety-meeting/history?${params.toString()}`);

      if (data?.error) {
        if (String(data.error).toLowerCase().includes('permission')) {
          setHasAccess(false);
          setMeetings([]);
          setTotal(0);
          return;
        }
        throw new Error(data.error);
      }

      setHasAccess(true);
      setMeetings(data.meetings || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.meetings || []).length);
    } catch (err) {
      setMeetings([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : 'Safety read load failed');
    } finally {
      setLoading(false);
    }
  }, [terminalFilter, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo<SafetySummary>(() => {
    const now = Date.now();
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

    let open = 0;
    let closed = 0;
    let totalSignatures = 0;
    let last30d = 0;
    let latestOpenedAt: number | null = null;
    const terminalsCovered = new Set<string>();

    meetings.forEach((m) => {
      const status = (m.status || '').toLowerCase();
      if (status === 'open') open++;
      else if (status === 'closed') closed++;

      totalSignatures += m.signature_count || 0;
      if (m.terminal_number) terminalsCovered.add(String(m.terminal_number));

      if (m.opened_at) {
        const t = new Date(m.opened_at).getTime();
        if (!Number.isNaN(t)) {
          if (now - t <= THIRTY_DAYS_MS) last30d++;
          if (latestOpenedAt == null || t > latestOpenedAt) latestOpenedAt = t;
        }
      }
    });

    return {
      total: meetings.length,
      open,
      closed,
      totalSignatures,
      last30d,
      terminalsCovered: terminalsCovered.size,
      latestOpenedAt,
    };
  }, [meetings]);

  return {
    meetings,
    total,
    terminalOptions,
    summary,
    loading,
    error,
    hasAccess,
    refreshSafety: load,
  };
}
